'use client'

// CommandPalette.js — ⌘K / Ctrl+K quick-search dialog
// Teaching source: docs/superpowers/specs/2026-04-20-admin-ux-overhaul-design.md §7.6
// Combines nav fuzzy search (via lib/adminFuzzySearch) + DB search (/api/admin/search).
//
// Keyboard:
//   ⌘K / Ctrl+K (window) — toggle open
//   Esc (via useFocusTrap) — close
//   ArrowUp / ArrowDown (input) — highlight item
//   Enter (input) — navigate to highlighted item
//
// DB search debounced 200ms to avoid hammering on keystroke.
// Items without href are filtered at the search layer.

import { useState, useEffect, useRef, useCallback } from 'react'
import { useRouter } from 'next/navigation'
import { Search, X, ArrowRight } from 'lucide-react'
import useFocusTrap from './hooks/useFocusTrap'
import { searchNavItems } from '@/lib/adminFuzzySearch'
import { getAdminHeaders } from '@/lib/artworkApi'

export default function CommandPalette() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [navResults, setNavResults] = useState([])
  const [dbResults, setDbResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef(null)
  const debounceRef = useRef(null)
  const router = useRouter()

  // Combined results: nav items first, then DB results
  const allResults = [...navResults, ...dbResults]

  // ── Global ⌘K / Ctrl+K toggle ───────────────────────────────────────────
  useEffect(() => {
    function handleGlobalKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setOpen(prev => !prev)
      }
    }
    window.addEventListener('keydown', handleGlobalKey)
    return () => window.removeEventListener('keydown', handleGlobalKey)
  }, [])

  // ── Focus input on open ──────────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      setQuery('')
      setNavResults([])
      setDbResults([])
      setActiveIndex(0)
      // Defer to next tick so useFocusTrap doesn't steal focus first
      setTimeout(() => inputRef.current?.focus(), 10)
    }
  }, [open])

  // ── Fuzzy nav search (synchronous, no debounce needed) ──────────────────
  useEffect(() => {
    if (!query.trim()) {
      setNavResults([])
      return
    }
    const matches = searchNavItems(query)
    setNavResults(matches)
    setActiveIndex(0)
  }, [query])

  // ── DB search (debounced 200ms) ──────────────────────────────────────────
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (!query || query.trim().length < 2) {
      setDbResults([])
      setLoading(false)
      return
    }

    setLoading(true)
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/admin/search?q=${encodeURIComponent(query.trim())}`, {
          credentials: 'include',
          headers: getAdminHeaders(),
        })
        if (!res.ok) {
          setDbResults([])
          return
        }
        const data = await res.json()
        setDbResults(data.results || [])
        setActiveIndex(0)
      } catch {
        setDbResults([])
      } finally {
        setLoading(false)
      }
    }, 200)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query])

  // ── Navigate to result ───────────────────────────────────────────────────
  const navigateTo = useCallback((item) => {
    if (!item?.href) return
    router.push(item.href)
    setOpen(false)
  }, [router])

  // ── Keyboard navigation on the INPUT (not window) ───────────────────────
  function handleInputKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex(i => Math.min(i + 1, allResults.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const item = allResults[activeIndex]
      if (item) navigateTo(item)
    }
  }

  // ── Focus trap (handles Esc + Tab cycling) ───────────────────────────────
  const dialogRef = useFocusTrap({ active: open, onEscape: () => setOpen(false) })

  const hasQuery = query.trim().length > 0
  const noResults = hasQuery && query.trim().length >= 2 && !loading && allResults.length === 0

  if (!open) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="admin-cmd-backdrop"
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="快速搜尋"
        data-tour="command-palette"
        className="admin-cmd-dialog"
      >
        {/* Search input row */}
        <div className="admin-cmd-input-row">
          <Search
            size={18}
            strokeWidth={1.75}
            className="admin-cmd-input-icon"
            aria-hidden="true"
          />
          <input
            ref={inputRef}
            type="search"
            className="admin-cmd-input"
            placeholder="搜尋頁面、訂單、作品…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleInputKeyDown}
            autoComplete="off"
            spellCheck={false}
            aria-label="快速搜尋"
            aria-autocomplete="list"
            aria-controls="admin-cmd-results"
            aria-activedescendant={
              allResults[activeIndex] ? `cmd-item-${activeIndex}` : undefined
            }
          />
          {query && (
            <button
              type="button"
              className="admin-cmd-clear"
              onClick={() => { setQuery(''); inputRef.current?.focus() }}
              aria-label="清除搜尋"
            >
              <X size={14} strokeWidth={2} aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            className="admin-cmd-close"
            onClick={() => setOpen(false)}
            aria-label="關閉搜尋"
          >
            <kbd>Esc</kbd>
          </button>
        </div>

        {/* Results list */}
        <ul
          id="admin-cmd-results"
          role="listbox"
          className="admin-cmd-results"
          aria-label="搜尋結果"
        >
          {/* Nav section */}
          {navResults.length > 0 && (
            <li className="admin-cmd-section-label" role="presentation">
              <span>頁面</span>
            </li>
          )}
          {navResults.map((item, idx) => (
            <li
              key={`nav-${item.id}`}
              id={`cmd-item-${idx}`}
              role="option"
              aria-selected={activeIndex === idx}
              className={`admin-cmd-item${activeIndex === idx ? ' admin-cmd-item--active' : ''}`}
              onClick={() => navigateTo(item)}
              onMouseEnter={() => setActiveIndex(idx)}
            >
              <span className="admin-cmd-item__label">{item.label}</span>
              <ArrowRight size={14} strokeWidth={1.75} className="admin-cmd-item__arrow" aria-hidden="true" />
            </li>
          ))}

          {/* DB section */}
          {dbResults.length > 0 && (
            <li className="admin-cmd-section-label" role="presentation">
              <span>資料</span>
            </li>
          )}
          {dbResults.map((item, idx) => {
            const globalIdx = navResults.length + idx
            return (
              <li
                key={`db-${item.id}`}
                id={`cmd-item-${globalIdx}`}
                role="option"
                aria-selected={activeIndex === globalIdx}
                className={`admin-cmd-item${activeIndex === globalIdx ? ' admin-cmd-item--active' : ''}`}
                onClick={() => navigateTo(item)}
                onMouseEnter={() => setActiveIndex(globalIdx)}
              >
                <span className="admin-cmd-item__label">{item.label}</span>
                {item.subtitle && (
                  <span className="admin-cmd-item__subtitle">{item.subtitle}</span>
                )}
                <ArrowRight size={14} strokeWidth={1.75} className="admin-cmd-item__arrow" aria-hidden="true" />
              </li>
            )
          })}

          {/* Loading */}
          {loading && (
            <li className="admin-cmd-status" role="presentation">
              <span>搜尋中…</span>
            </li>
          )}

          {/* Empty state */}
          {noResults && (
            <li className="admin-cmd-empty" role="presentation">
              <span>沒有找到「{query}」</span>
            </li>
          )}

          {/* Hint when no query yet */}
          {!hasQuery && (
            <li className="admin-cmd-hint" role="presentation">
              <span>輸入頁面名稱、訂單編號、或作品名稱</span>
            </li>
          )}
        </ul>
      </div>
    </>
  )
}
